import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { CaretRight, CornersIn, CornersOut } from '@phosphor-icons/react';
import { cashRunway, healthStatus } from '../simulation/health';
import { companyScore } from '../game/objectives';
import type { GameState } from '../game/types';
import type { RoomSnapshot } from '../net/roomClient';
import { financialMoney, financialUnitFor, money } from '../ui/format';
import { useFullscreen } from '../hooks/useFullscreen';
import { useI18n } from '../i18n';

const HEALTH_LABEL = {
  healthy: 'ui.经营健康',
  warning: 'ui.需要关注',
  crisis: 'ui.现金危机',
  restructuring: 'ui.需要重组',
};

interface TopBarProps {
  game: GameState;
  onAdvance: () => void;
  room?: RoomSnapshot | null;
  roomLocked?: boolean;
}

export function TopBar({ game, onAdvance, room, roomLocked }: TopBarProps) {
  const [showCashDetail, setShowCashDetail] = useState(false);
  const [advancePending, setAdvancePending] = useState(false);
  const advanceLockRef = useRef(false);
  const { t } = useI18n();
  const { isFullscreen, isSupported, toggle } = useFullscreen();
  const report = game.lastReport;
  const cashDetailUnit = financialUnitFor(report ? [report.operatingProfit, report.interest, report.principalPaid, report.netCashFlow] : []);
  const status = healthStatus(game);
  const runway = cashRunway(game);
  const score = companyScore(game).total;
  const readyCount = room?.players.filter((player) => player.ready).length ?? 0;
  const advanceDisabled = !!game.gameOver || !!game.activeAuction || !!roomLocked || advancePending;
  useEffect(() => {
    advanceLockRef.current = false;
    setAdvancePending(false);
  }, [game.week, room?.phase, roomLocked]);
  const handleAdvance = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (advanceDisabled || advanceLockRef.current) return;
    // 先同步锁定真实 DOM，再等待 React 状态更新，拦截同一触摸序列中的快速二次点击。
    event.currentTarget.disabled = true;
    advanceLockRef.current = true;
    setAdvancePending(true);
    onAdvance();
  };
  const advanceLabel = game.gameOver
    ? t('ui.已结束')
    : room
      ? room.phase === 'lobby'
        ? t('ui.未开始')
        : roomLocked
          ? room.phase === 'processing'
            ? t('ui.结算中…')
            : t('ui.等待 {ready}/{total}', { ready: readyCount, total: room.players.length })
          : t('ui.下一周')
      : t('ui.下一周');

  return (
    <header className="topbar">
      <div className="brand-block">
        <div className="brand-mark">NF</div>
        <div>
          <div className="brand-name">NoFlight</div>
          <div className="week-label">第 {game.week} 周 · {game.companyName}</div>
        </div>
        {isSupported && (
          <button
            type="button"
            className="icon-button fullscreen-button"
            onClick={toggle}
            aria-label={isFullscreen ? t('ui.退出全屏') : t('ui.进入全屏')}
            title={isFullscreen ? t('ui.退出全屏') : t('ui.进入全屏')}
          >
            {isFullscreen ? <CornersIn weight="bold" size={16} /> : <CornersOut weight="bold" size={16} />}
          </button>
        )}
      </div>
      <div className="metrics-strip" aria-label={t('ui.经营指标')}>
        <div className="metric metric-popover-anchor" data-tutorial-finance-cash>
          <button className="metric-button" onClick={() => setShowCashDetail((current) => !current)} aria-expanded={showCashDetail}>
            <span>{t('ui.现金')}</span>
            <strong>{money(game.cash)}</strong>
          </button>
          {showCashDetail && (
            <div className="cash-detail-popover">
              <strong>{t('ui.本周现金变化')}</strong>
              {report ? (
                <>
                  <div><span>{t('ui.经营利润')}</span><b className={report.operatingProfit < 0 ? 'red' : 'green'}>{financialMoney(report.operatingProfit, cashDetailUnit)}</b></div>
                  <div><span>{t('ui.贷款利息')}</span><b className="red">−{financialMoney(report.interest, cashDetailUnit)}</b></div>
                  <div><span>{t('ui.偿还本金')}</span><b className="red">−{financialMoney(report.principalPaid, cashDetailUnit)}</b></div>
                  <div className="total"><span>{t('ui.净现金流')}</span><b className={report.netCashFlow < 0 ? 'red' : 'green'}>{financialMoney(report.netCashFlow, cashDetailUnit)}</b></div>
                </>
              ) : (
                <p>{t('ui.推进一周后会显示现金流拆分。')}</p>
              )}
            </div>
          )}
        </div>
        <div className={`metric ${(report?.netCashFlow ?? 0) < 0 ? 'negative' : 'positive'}`} data-tutorial-finance-net-cash-flow>
          <span>{t('ui.净现金流')}</span>
          <strong>{report ? money(report.netCashFlow) : '-'}</strong>
        </div>
        <div className={`metric ${(report?.operatingProfit ?? 0) < 0 ? 'negative' : 'positive'}`} data-tutorial-finance-operating-profit>
          <span>{t('ui.经营利润')}</span>
          <strong>{report ? money(report.operatingProfit) : '-'}</strong>
        </div>
        <div className="metric" data-tutorial-finance-runway>
          <span>{t('ui.现金安全期')}</span>
          <strong>{runway > 99 ? t('ui.99+周') : t('ui.{weeks}周', { weeks: runway.toFixed(1) })}</strong>
        </div>
        <div className="metric metric-reputation">
          <span>{t('ui.声誉')}</span>
          <strong>{game.reputation.toFixed(1)}</strong>
        </div>
        <div className="metric metric-score">
          <span>{t('ui.评分')}</span>
          <strong>{score}</strong>
        </div>
      </div>
      <div className="advance-wrap">
        <div className={`health-pill ${status}`}>{t(HEALTH_LABEL[status])}</div>
        <button className="advance-button" data-tutorial-advance-week onClick={handleAdvance} disabled={advanceDisabled} aria-busy={advancePending} aria-label={t('ui.推进一周')}>
          <span>{advanceLabel}</span>
          <CaretRight weight="bold" size={22} />
        </button>
      </div>
    </header>
  );
}
